import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { NegotiationTemplate, TemplateKind } from "@/types/negotiations";

const QUERY_KEY = ["negotiation_templates"] as const;

async function fetchTemplates(): Promise<NegotiationTemplate[]> {
  const { data, error } = await supabase
    .from("negotiation_templates" as any)
    .select("*")
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as unknown as NegotiationTemplate[];
}

export type CreateTemplateInput = {
  name: string;
  kind: TemplateKind;
  tone?: string | null;
  target_discount_pct?: number | null;
  floor_apr?: number | null;
  body_guidance?: string | null;
};

export type UpdateTemplateInput = Partial<CreateTemplateInput>;

export function useNegotiationTemplates() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    data: templates = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchTemplates,
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }

  const createMutation = useMutation({
    mutationFn: async (input: CreateTemplateInput): Promise<NegotiationTemplate> => {
      const { data, error } = await supabase
        .from("negotiation_templates" as any)
        .insert({
          name: input.name,
          kind: input.kind,
          tone: input.tone ?? null,
          target_discount_pct: input.target_discount_pct ?? null,
          floor_apr: input.floor_apr ?? null,
          body_guidance: input.body_guidance ?? null,
        })
        .select()
        .single();

      if (error) throw error;
      return data as unknown as NegotiationTemplate;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Template created" });
    },
    onError: (err) => {
      toast({
        title: "Failed to create template",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateTemplateInput;
    }): Promise<NegotiationTemplate> => {
      const { data, error } = await supabase
        .from("negotiation_templates" as any)
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as NegotiationTemplate;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Template updated" });
    },
    onError: (err) => {
      toast({
        title: "Failed to update template",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase
        .from("negotiation_templates" as any)
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Template deleted" });
    },
    onError: (err) => {
      toast({
        title: "Failed to delete template",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  return {
    templates,
    isLoading,
    error,
    refetch,

    /** Create a new negotiation template. */
    createTemplate: (input: CreateTemplateInput) => createMutation.mutateAsync(input),

    /** Update an existing template by id. */
    updateTemplate: (id: string, updates: UpdateTemplateInput) =>
      updateMutation.mutateAsync({ id, updates }),

    /** Delete a template by id. */
    deleteTemplate: (id: string) => deleteMutation.mutateAsync(id),

    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
